import { EntityAdapter } from '../../adapters/interfaces/entity.adapter';
import { GroupAdapter } from '../../adapters/interfaces/group.adapter';
import { NodeAdapter } from '../../adapters/interfaces/node.adapter';
import { CheckState } from '../../enum/check-state.enum';
import { EntryNode } from './entry-node';
import { SearchIndex } from './search-index';
import { defaultSearchMatcher, SearchMatcher } from './search-matcher';
import { CommonUtils } from '../../tool/common-utils';

export type EntryNodeComparator<T> = (
  a: EntryNode<T>,
  b: EntryNode<T>,
) => number;

function isNodeAdapter<T>(
  adapter: EntityAdapter<T>,
): adapter is NodeAdapter<T> {
  return typeof (adapter as Partial<NodeAdapter<T>>).getChildren === 'function';
}

function isGroupAdapter<T>(
  adapter: EntityAdapter<T>,
): adapter is GroupAdapter<T> {
  return typeof (adapter as Partial<GroupAdapter<T>>).getGroupId === 'function';
}

function normalizeSearchIndex(index: SearchIndex): SearchIndex {
  return {
    primary: CommonUtils.normalize(index.primary, true),
    secondary: index.secondary?.map((value) =>
      CommonUtils.normalize(value, true),
    ),
  };
}

function buildNode<T>(
  entity: T,
  adapter: EntityAdapter<T>,
  parent: EntryNode<T> | undefined,
): EntryNode<T> {
  const node = new EntryNode<T>({
    id: adapter.getId(entity),
    entity,
    synthetic: false,
    parent,
    searchIndex: normalizeSearchIndex(adapter.getSearchIndex(entity)),
    label: adapter.getLabel(entity),
    icon: adapter.getIcon?.(entity),
  });
  if (isNodeAdapter(adapter)) {
    node.children = adapter
      .getChildren(entity)
      .map((child) => buildNode(child, adapter, node));
  }
  return node;
}

function buildGroupedRoots<T>(
  data: readonly T[],
  adapter: GroupAdapter<T>,
): EntryNode<T>[] {
  const groups = new Map<unknown, { label: string; entities: T[] }>();
  for (const entity of data) {
    const groupId = adapter.getGroupId(entity);
    const group = groups.get(groupId);
    if (group) {
      group.entities.push(entity);
    } else {
      groups.set(groupId, {
        label: adapter.getGroupLabel(entity),
        entities: [entity],
      });
    }
  }
  return [...groups.entries()]
    .sort(([, a], [, b]) => a.label.localeCompare(b.label))
    .map(([groupId, group]) => {
      const groupNode = new EntryNode<T>({
        id: groupId,
        synthetic: true,
        searchIndex: normalizeSearchIndex({ primary: group.label }),
        label: group.label,
      });
      groupNode.children = group.entities.map((entity) =>
        buildNode(entity, adapter, groupNode),
      );
      groupNode.expand();
      return groupNode;
    });
}

function collectVisibleNodes<T>(
  nodes: readonly EntryNode<T>[],
  searchActive: boolean,
  comparator: EntryNodeComparator<T> | undefined,
  result: EntryNode<T>[],
): void {
  const ordered = comparator ? [...nodes].sort(comparator) : nodes;
  for (const node of ordered) {
    if (!node.visible) {
      continue;
    }
    result.push(node);
    if (searchActive || node.isExpanded()) {
      collectVisibleNodes(node.children, searchActive, comparator, result);
    }
  }
}

export class EntryTree<T> {
  private constructor(public readonly roots: readonly EntryNode<T>[]) {}

  public static build<T>(
    data: readonly T[],
    adapter: EntityAdapter<T> | NodeAdapter<T> | GroupAdapter<T>,
  ): EntryTree<T> {
    const roots = isGroupAdapter(adapter)
      ? buildGroupedRoots(data, adapter)
      : data.map((entity) => buildNode(entity, adapter, undefined));
    return new EntryTree<T>(roots);
  }

  public applySearch(
    query: string,
    matcher: SearchMatcher = defaultSearchMatcher,
  ): void {
    const normalized = CommonUtils.normalize(query, true);
    this.roots.forEach((root) => root.applySearch(normalized, matcher, false));
  }

  public flattenVisible(
    searchActive = false,
    comparator?: EntryNodeComparator<T>,
  ): EntryNode<T>[] {
    const result: EntryNode<T>[] = [];
    collectVisibleNodes(this.roots, searchActive, comparator, result);
    return result;
  }

  public getCheckedLeafEntities(): T[] {
    return this.roots.flatMap((node) => node.getCheckedLeafEntities());
  }

  public getCheckedGroupEntities(): T[] {
    return this.roots.flatMap((node) => node.getCheckedGroups());
  }

  public checkEntities(entities: ReadonlySet<T>): void {
    this.roots.forEach((root) => root.checkEntities(entities));
  }

  public get checkState(): CheckState {
    const states = this.roots.map((item) => item.checkState);
    if (states.every((state) => state === CheckState.check)) {
      return CheckState.check;
    }
    if (states.every((state) => state === CheckState.uncheck)) {
      return CheckState.uncheck;
    }
    return CheckState.partial;
  }

  public toggleSelectAll(): void {
    if (this.checkState === CheckState.check) {
      this.uncheckAll();
      return;
    }
    this.checkAll();
  }

  private checkAll(): void {
    this.roots.forEach((node) => node.check());
  }

  private uncheckAll(): void {
    this.roots.forEach((node) => node.uncheck());
  }

  public applySelectionFilter(displayOnlySelected: boolean): void {
    this.roots.forEach((node) =>
      node.toggleVisibilityByCheckState(displayOnlySelected),
    );
  }
}
